const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const cleaned = String(v).replace('%', '').trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

export const isGovtSecurity = (record) =>
  record?.securityType === 'Central Government Securities'
  || record?.securityType === 'State Government Securities';

export const getGovtIssuerDisplayName = (record) => {
  if (!record) return '—';
  if (record.securityType === 'State Government Securities') {
    const state =
      (record.stateGovernment && String(record.stateGovernment).trim())
      || (record.companyIssuerName && String(record.companyIssuerName).trim());
    return state || '—';
  }
  if (record.securityType === 'Central Government Securities') {
    return 'Government of India';
  }
  return record.companyIssuerName || record.issuerName || '—';
};

export const getIssuerDisplayName = (record) => {
  if (!record) return '—';
  if (isGovtSecurity(record)) return getGovtIssuerDisplayName(record);

  const candidates = [
    record.companyIssuerName,
    record.issuerName,
    record.issuer,
    record.name,
  ];
  const name = candidates
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  return name || '—';
};

export const getCouponValueFromNomenclature = (record) => {
  if (!record?.nomenclature) return null;
  const firstPart = record.nomenclature.trim().split(/\s+/)[0];
  if (firstPart && firstPart.includes('%')) {
    return firstPart.replace('%', '');
  }
  return null;
};

export const getCouponDisplay = (record) => {
  if (isGovtSecurity(record)) {
    const val = getCouponValueFromNomenclature(record);
    if (val) {
      const num = parseFloat(val);
      return Number.isFinite(num) ? `${num.toFixed(2)}%` : `${val}%`;
    }
  }
  const raw = record?.couponRate ?? record?.coupon;
  if (raw == null || raw === '') return '—';
  const str = String(raw).replace(/%/g, '').trim();
  const num = parseFloat(str);
  return Number.isFinite(num) ? `${num.toFixed(2)}%` : str;
};

export const getMaturityYearFromNomenclature = (record) => {
  if (!record?.nomenclature) return null;
  try {
    const parts = record.nomenclature.trim().split(/\s+/);
    for (let offset = 1; offset <= 3; offset += 1) {
      const candidate = parts[parts.length - offset];
      if (candidate && !Number.isNaN(parseInt(candidate, 10))) return candidate;
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const getMaturityDisplay = (record) => {
  if (isGovtSecurity(record)) {
    return record.dateOfMaturity
      || record.redemption
      || getMaturityYearFromNomenclature(record)
      || '—';
  }
  return record.redemption || record.dateOfRedemption || '—';
};

export const getIssueDateDisplay = (record) => {
  if (isGovtSecurity(record)) {
    return record.dateOfIssue || record.allotmentDate || '—';
  }
  return record.allotmentDate || record.issueDate || '—';
};

export const getPaymentFrequency = (record) =>
  record?.interestPaymentFrequency || record?.couponPaymentFrequency || '—';

const normalizeRatingKey = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      if (!item) continue;
      if (typeof item === 'string') {
        const s = item.trim();
        if (s) return s;
      } else if (typeof item === 'object') {
        const s = String(item.currentRating || item.rating || '').trim();
        if (s) return s;
      }
    }
    return null;
  }
  const str = String(value).trim();
  if (!str) return null;
  return str.split(/[,|/]/)[0]?.trim() || str;
};

export const getRatingDisplay = (record) => {
  if (isGovtSecurity(record)) {
    if (record.securityType === 'State Government Securities') return 'SDL';
    return 'Sovereign';
  }
  return normalizeRatingKey(
    record.creditRatings || record.creditRating || record.rating,
  ) || '—';
};

export const getSecurityTypeBadge = (record) => {
  if (record.securityType === 'Central Government Securities') return 'G-Sec';
  if (record.securityType === 'State Government Securities') return 'SDL';
  const st = (record.securityType || '').trim();
  if (!st) return 'Corporate';
  if (/corporate/i.test(st)) return 'Corporate';
  return st.replace(/_/g, ' ');
};

export { toNumberOrNull };
